import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { AUTH_FAILED_CONN, connectToAuth } from "./connectToAuth";
import { createNotificationSetter } from "@zapier/canvas-state";

export const useCreateAppConnection = (accountId: number) => {
  const createNotification = useSetAtom(createNotificationSetter);

  const createNewConnection = useCallback(
    async function createNewConnection(app: any) {
      try {
        const selectedApi = app.id;
        if (!app?.authType) {
          throw new Error(`${app?.name} does not require connections`);
        }
        // This function is an interface with the engine team to open a popup
        // to a url that handles creating new auth connections to a service
        // defined by the `serviceKey` value passed in. This can be something like
        // TwitterAPI or TwitterAPI@1.0.0
        const id = await connectToAuth({
          accountId,
          authId: undefined,
          authIdentifier: undefined,
          destinationServiceKey: undefined,
          hasUpfrontFields: Boolean(app?.hasUpfrontFields),
          isOauth: app.authType === "oauth",
          isShared: undefined,
          serviceKey: selectedApi || app.id || undefined,
        });

        return { app, id, success: true };
      } catch (error) {
        const errorMessage = (error as any)?.message || error?.toString();

        // If the error is an `AUTH_FAILED_CONN` error then the user closed
        // the popup. No need to inform them that the auth didn't connect
        // since they initiated the flow.
        if ((error as any)?.code !== AUTH_FAILED_CONN) {
          // Handle popup blocker errors with helpful messaging
          if (
            errorMessage?.includes("Popup blocked") ||
            errorMessage?.includes("blocked by your browser")
          ) {
            createNotification({
              message:
                "Popup blocked! Please allow popups for Zapier and try again.",
              type: "error",
            });
          } else if (!errorMessage?.includes("Popup was blocked")) {
            // Show other errors (but not generic popup blocked messages)
            createNotification({
              message: errorMessage,
              type: "error",
            });
          }
        }

        return { app, success: false, error };
      }
    },
    [accountId, createNotification],
  );

  return { createNewConnection };
};
